import { defaultDataLayerColor, hexToRGBA } from '@/utils/colors'
import { TDataLayerStyle } from '@lifk/types'
import { isNumber } from '@lifk/utils'
import { ExpressionSpecification } from 'mapbox-gl'

interface IColorConfig {
  filter?: ExpressionSpecification
  color: string
  opacity?: number
  style: TDataLayerStyle
  minValue?: number
  maxValue?: number
  targetAttribute?: string
}

export const getMapboxColorExpression = (
  config: IColorConfig[],
  colorByFilter: boolean,
  isGradient: boolean
) => {
  let paintColors: string | ExpressionSpecification = defaultDataLayerColor

  if (config.length === 1) {
    if (config[0].targetAttribute === 'color') {
      // only for native lands layer
      paintColors = ['get', config[0].targetAttribute]
    } else {
      paintColors = config[0].color
    }
  } else {
    paintColors = getColorCaseStatement(config, colorByFilter, isGradient)
  }

  return paintColors
}

const getColorCaseStatement = (
  config: IColorConfig[],
  colorByFilter: boolean,
  isGradient: boolean
) => {
  if (colorByFilter) {
    return getCategoricalColors(config)
  } else if (
    config.every(
      ({ minValue, maxValue }) => isNumber(minValue) && isNumber(maxValue)
    )
  ) {
    if (config.every(({ style }) => style === 'Raster')) {
      return getRasterColors(config)
    } else if (config.every(({ style }) => style === 'Animated (particles)')) {
      return getAnimatedParticlesColors(config)
    } else if (
      config.every(
        ({ targetAttribute }) => targetAttribute && targetAttribute.length > 0
      )
    ) {
      if (isGradient) {
        return getGradientColors(config)
      } else {
        return getBinnedColors(config)
      }
    }
  }

  console.warn('Failed to generate color expression: not enough params')
  return config[0].color
}

/*
  Rasters include opacity in the color expression, unlike Vectors.
  Vector layers use a separate opacity expression,
    which allows for dynamic hover opacity.
*/
const getRasterColors = (config: IColorConfig[]) => {
  const interpolateExpr: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['raster-value']
  ]
  config.forEach((colorStop) => {
    interpolateExpr.push(colorStop.maxValue)
    const colorWithOpacity = isNumber(colorStop.opacity)
      ? hexToRGBA(colorStop.color, colorStop.opacity as number)
      : colorStop.color
    interpolateExpr.push(colorWithOpacity)
  })
  return interpolateExpr
}

const getAnimatedParticlesColors = (config: IColorConfig[]) => {
  const interpolateExpr: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['raster-particle-speed']
  ]
  config.forEach((colorStop) => {
    interpolateExpr.push(colorStop.maxValue)
    interpolateExpr.push(colorStop.color)
  })
  return interpolateExpr
}

const getCategoricalColors = (config: IColorConfig[]) => {
  const caseStatement: ExpressionSpecification = ['case']
  config.forEach((colorStop) => {
    caseStatement.push(colorStop.filter)
    caseStatement.push(colorStop.color)
  })
  // necessary for mapbox as the default color
  // Values should be filtered out so we shouldn't get to this default
  caseStatement.push(defaultDataLayerColor)
  return caseStatement
}

const getGradientColors = (config: IColorConfig[]) => {
  const interpolateExpr: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['get', config[0].targetAttribute]
  ]
  config.forEach((colorStop) => {
    interpolateExpr.push(colorStop.maxValue)
    interpolateExpr.push(colorStop.color)
  })
  return interpolateExpr
}

const getBinnedColors = (config: IColorConfig[]) => {
  const caseStatement: ExpressionSpecification = [
    'case',
    ['<=', ['get', config[0].targetAttribute], 0],
    'rgba(0,0,0,0)'
  ]
  config.forEach((colorStop, index) => {
    // don't add filter for last item (use last color as case statement default)
    if (colorStop.maxValue && index + 1 < config.length) {
      caseStatement.push([
        '<',
        ['get', colorStop.targetAttribute],
        colorStop.maxValue
      ])
    }
    caseStatement.push(colorStop.color)
  })
  return caseStatement
}
